import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Lock, Ticket, Phone, ShieldCheck } from "lucide-react";

export default function AuthDialog() {
  const { dialog, closeAuth, login, register, refresh } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("register");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [smsCode, setSmsCode] = useState("");
  const [countdown, setCountdown] = useState(0);
  const [cardKey, setCardKey] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (dialog.open) {
      setMode(dialog.mode || "register");
      setCardKey(dialog.cardKey || "");
      setInviteCode(dialog.inviteCode || "");
      setErr("");
      setPhone("");
      setPassword("");
      setSmsCode("");
      setCountdown(0);
    }
  }, [dialog.open, dialog.mode, dialog.cardKey, dialog.inviteCode]);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  const sendSms = async () => {
    if (!/^1[3-9]\d{9}$/.test(phone)) {
      toast.error("请先输入正确的11位手机号");
      return;
    }
    try {
      const { data } = await api.post("/sms/send", { phone, purpose: mode === "reset" ? "reset" : "register" });
      if (data.demo) {
        setSmsCode(data.dev_code);
        toast.success(`演示模式，验证码已自动填入：${data.dev_code}`);
      } else {
        toast.success("验证码已发送，请注意查收短信");
      }
      setCountdown(60);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      if (mode === "register") {
        await register({ phone: phone.trim(), password, sms_code: smsCode.trim(), invite_code: inviteCode.trim() || undefined });
        if (cardKey.trim()) {
          try {
            await api.post("/keys/redeem", { card_key: cardKey.trim() });
            await refresh();
            toast.success("注册成功，卡密已激活，欢迎加入合赢！");
          } catch (e2) {
            toast.error(`注册成功，但卡密激活失败：${apiError(e2)}，可在个人中心重新激活`);
          }
        } else {
          toast.success("注册成功！激活卡密后即可开启邀请奖励");
        }
        closeAuth();
        navigate("/me");
      } else if (mode === "reset") {
        await api.post("/auth/reset-password", { phone: phone.trim(), sms_code: smsCode.trim(), new_password: password });
        toast.success("密码已重置，请使用新密码登录");
        setMode("login");
        setPassword("");
        setSmsCode("");
      } else {
        await login(phone.trim(), password);
        toast.success("登录成功，欢迎回来");
        closeAuth();
      }
    } catch (e2) {
      setErr(apiError(e2));
    } finally {
      setBusy(false);
    }
  };

  const title = mode === "register" ? "手机号注册 · 加入合赢" : mode === "reset" ? "找回密码" : "会员登录";

  return (
    <Dialog open={dialog.open} onOpenChange={(o) => !o && closeAuth()}>
      <DialogContent className="bg-[#0F0F14] border border-[#D4AF37]/25 text-[#F8F9FA] sm:max-w-md" data-testid="auth-dialog">
        <DialogTitle className="font-serif-sc text-xl tracking-widest text-center">{title}</DialogTitle>
        {mode === "reset" ? (
          <button
            type="button"
            data-testid="auth-back-login-btn"
            onClick={() => {
              setMode("login");
              setErr("");
            }}
            className="text-xs text-gray-500 hover:text-[#D4AF37] transition-colors text-center mt-1"
          >
            ← 返回登录
          </button>
        ) : (
          <div className="grid grid-cols-2 gap-2 mt-2">
            <button
              data-testid="auth-tab-register"
              onClick={() => setMode("register")}
              className={`py-2 text-sm tracking-widest border transition-colors ${
                mode === "register" ? "border-[#D4AF37]/60 text-[#D4AF37]" : "border-[#D4AF37]/15 text-gray-500"
              }`}
            >
              手机号注册
            </button>
            <button
              data-testid="auth-tab-login"
              onClick={() => setMode("login")}
              className={`py-2 text-sm tracking-widest border transition-colors ${
                mode === "login" ? "border-[#D4AF37]/60 text-[#D4AF37]" : "border-[#D4AF37]/15 text-gray-500"
              }`}
            >
              会员登录
            </button>
          </div>
        )}
        <form onSubmit={submit} className="mt-4 flex flex-col gap-3">
          <label className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            {mode === "login" ? (
              <input
                data-testid="auth-login-phone-input"
                className="input-gold w-full pl-10 pr-3 py-2.5 text-sm font-mono-num"
                placeholder="手机号 / 用户名"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
              />
            ) : (
              <input
                data-testid="auth-phone-input"
                className="input-gold w-full pl-10 pr-3 py-2.5 text-sm font-mono-num"
                placeholder="手机号码（11位）"
                inputMode="numeric"
                maxLength={11}
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                required
              />
            )}
          </label>
          {(mode === "register" || mode === "reset") && (
            <div className="flex gap-2">
              <label className="relative flex-1">
                <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  data-testid="auth-sms-code-input"
                  className="input-gold w-full pl-10 pr-3 py-2.5 text-sm font-mono-num tracking-widest"
                  placeholder="短信验证码"
                  inputMode="numeric"
                  maxLength={6}
                  value={smsCode}
                  onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, ""))}
                  required
                />
              </label>
              <button
                type="button"
                data-testid="send-sms-btn"
                onClick={sendSms}
                disabled={countdown > 0}
                className="btn-ghost-gold px-4 text-xs whitespace-nowrap disabled:opacity-40"
              >
                {countdown > 0 ? `${countdown}s后重发` : "获取验证码"}
              </button>
            </div>
          )}
          <label className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              data-testid="auth-password-input"
              type="password"
              className="input-gold w-full pl-10 pr-3 py-2.5 text-sm"
              placeholder={mode === "reset" ? "新密码（至少6位）" : "密码（至少6位）"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </label>
          {mode === "register" && (
            <label className="relative">
              <Ticket className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                data-testid="auth-invite-code-input"
                className="input-gold w-full pl-10 pr-3 py-2.5 text-sm font-mono-num tracking-wider uppercase"
                placeholder="邀请码（选填）"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
              />
            </label>
          )}
          {err && (
            <p data-testid="auth-error" className="text-sm text-red-400 border border-red-500/30 bg-red-500/10 px-3 py-2">
              {err}
            </p>
          )}
          <button data-testid="auth-submit-btn" type="submit" disabled={busy} className="btn-gold py-2.5 text-sm mt-1">
            {busy ? "处理中..." : mode === "register" ? "注 册" : mode === "reset" ? "重置密码" : "登 录"}
          </button>
          {mode === "login" && (
            <button
              type="button"
              data-testid="auth-forgot-link"
              onClick={() => {
                setMode("reset");
                setErr("");
                setPassword("");
              }}
              className="text-xs text-gray-500 hover:text-[#D4AF37] transition-colors text-center"
            >
              忘记密码？通过短信验证码找回
            </button>
          )}
        </form>
        {mode === "register" && (
          <p className="text-xs text-gray-500 text-center mt-3">
            注册完成后，可在个人中心激活淘宝购买的卡密成为正式会员
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
